import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest, promptInput } from './cli-helpers.js';

type CacheClearResponse = {
  ok: boolean;
  error?: string;
  invalidated?: number;
  mode?: string;
};

type WorkflowResetResponse = {
  ok: boolean;
  error?: string;
  nodesReset?: number;
};

/**
 * Pure helper: build the `cache-clear` request body from parsed argv flags.
 * Returns a discriminated result so callers can surface parse errors without
 * the helper itself writing to stderr or calling `process.exit`.
 */
export function buildCacheClearBody(opts: {
  nodeId?: string;
  belowVersionRaw?: string;
  staleOnly?: boolean;
  mode?: string;
}): { ok: true; body: Record<string, unknown> } | { ok: false; error: string } {
  const body: Record<string, unknown> = {};
  if (opts.nodeId) body.nodeId = opts.nodeId;
  if (opts.belowVersionRaw !== undefined) {
    const n = Number(opts.belowVersionRaw);
    if (!Number.isInteger(n) || n <= 0) {
      return {
        ok: false,
        error: `--below-version must be a positive integer, got "${opts.belowVersionRaw}"`,
      };
    }
    body.belowVersion = n;
  }
  if (opts.staleOnly) body.staleOnly = true;
  if (opts.mode) {
    if (opts.mode !== 'all' && opts.mode !== 'hard') {
      return {
        ok: false,
        error: `--mode must be "all" or "hard", got "${opts.mode}"`,
      };
    }
    body.mode = opts.mode;
  }

  const modeCount =
    (body.nodeId !== undefined ? 1 : 0) +
    (body.belowVersion !== undefined ? 1 : 0) +
    (body.staleOnly !== undefined ? 1 : 0) +
    (body.mode !== undefined ? 1 : 0);
  if (modeCount === 0) {
    return {
      ok: false,
      error:
        'cache clear requires one of: --node <id>, --below-version <n>, --stale-only, --mode all, or --mode hard',
    };
  }
  if (modeCount > 1) {
    return {
      ok: false,
      error: 'cache clear accepts exactly one of --node / --below-version / --stale-only / --mode',
    };
  }
  return { ok: true, body };
}

export async function handleClear(
  argv: string[],
  apiUrl: string,
  token: string,
  printUsage: () => void
): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Missing workflowId.');
    printUsage();
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');

  const built = buildCacheClearBody({
    nodeId: getArgValue(argv, '--node'),
    belowVersionRaw: getArgValue(argv, '--below-version'),
    staleOnly: hasFlag(argv, '--stale-only'),
    mode: getArgValue(argv, '--mode'),
  });
  if (!built.ok) {
    console.error(built.error);
    process.exit(1);
  }
  const body = built.body;

  if (body.mode === 'hard') {
    await handleHardReset(workflowId, hasFlag(argv, '--yes'), json, apiUrl, token);
    return;
  }

  const result = await apiRequest<CacheClearResponse>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/cache-clear`,
    body
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`Cleared (${result.mode}): ${result.invalidated ?? 0} cache row(s)`);
}

async function handleHardReset(
  workflowId: string,
  yesFlag: boolean,
  json: boolean,
  apiUrl: string,
  token: string
): Promise<void> {
  if (!yesFlag) {
    console.log(
      [
        '',
        `Hard reset on workflow ${workflowId} will:`,
        '  - DELETE all cached browser actions for this workflow',
        '  - REMOVE playbookSteps from every node config',
        '  - CLEAR the workflow lastRunId',
        '',
        'This is equivalent to the UI "Reset flow" button and cannot be undone.',
        '',
      ].join('\n')
    );
    const answer = await promptInput('Type "yes" to confirm');
    if (answer.toLowerCase() !== 'yes') {
      console.error('Aborted.');
      process.exit(1);
    }
  }

  const result = await apiRequest<WorkflowResetResponse>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/reset`
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(
    `Reset workflow: ${result.nodesReset ?? 0} node(s) cleared (cache rows deleted, playbook steps removed, lastRunId cleared)`
  );
}
